import React from 'react';
import { t, Lang } from '../../../../i18n';

// Bubble component for keywords and extracted values
const Bubble: React.FC<{ text: string; type: 'keyword' | 'extracted' }> = ({ text, type }) => (
  <span className={`bubble ${type}-bubble`}>
    {text}
  </span>
);

// Shared formatting function for measurements
function formatMeasurement(key: string, value: any, lang: Lang = 'en'): string {
  // Handle array of stringified JSON objects
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    try {
      const parsed = JSON.parse(value[0]);
      return formatMeasurement(key, parsed, lang);
    } catch (e) {
      // fallback to showing the string if parsing fails
      return `${t(lang, key as any)}: ${value[0]}`;
    }
  }
  // Special formatting for 'size'
  if (
    key === 'size' &&
    typeof value === 'object' && value !== null &&
    Array.isArray(value.values) &&
    value.unit
  ) {
    return `${t(lang, key as any)}: ${value.values.join(' x ')} ${value.unit}`;
  }
  // Special formatting for 'area'
  if (
    key === 'area' &&
    typeof value === 'object' && value !== null &&
    ('min' in value || 'max' in value)
  ) {
    const min = value.min !== undefined && value.min !== null ? Number(value.min) : undefined;
    const max = value.max !== undefined && value.max !== null ? Number(value.max) : undefined;
    let unit = value.unit;
    if ((!unit || unit === null) && value.type === 'area') {
      unit = 'm2';
    } else if (!unit) {
      unit = '';
    }
    if (min !== undefined && max !== undefined) {
      if (min === max) {
        return `${t(lang, key as any)}: ${min} ${unit}`;
      } else {
        return `${t(lang, key as any)}: ${min} ${unit} < ${t(lang, key as any)} < ${max} ${unit}`;
      }
    } else if (min !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} > ${min} ${unit}`;
    } else if (max !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} < ${max} ${unit}`;
    }
  }
  // Special formatting for 'height', 'diameter', 'depth', and 'length'
  if (
    (key === 'height' || key === 'diameter' || key === 'depth' || key === 'length') &&
    typeof value === 'object' && value !== null &&
    ('min' in value || 'max' in value)
  ) {
    const min = value.min !== undefined && value.min !== null ? Number(value.min) : undefined;
    const max = value.max !== undefined && value.max !== null ? Number(value.max) : undefined;
    let unit = value.unit || '';
    if (min !== undefined && max !== undefined) {
      if (min === max) {
        return `${t(lang, key as any)}: ${min} ${unit}`;
      } else {
        return `${t(lang, key as any)}: ${min} ${unit} < ${t(lang, key as any)} < ${max} ${unit}`;
      }
    } else if (min !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} > ${min} ${unit}`;
    } else if (max !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} < ${max} ${unit}`;
    }
  }
  // Fallbacks
  if (Array.isArray(value)) {
    return `${t(lang, key as any)}: ${value.map(v => Array.isArray(v) ? v.join(', ') : v).join(', ')}`;
  } else if (typeof value === 'object' && value !== null) {
    // Only fallback to JSON if not a min/max/unit object
    if (!('min' in value) && !('max' in value)) {
      return `${t(lang, key as any)}: ${JSON.stringify(value)}`;
    } else {
      return '';
    }
  } else {
    return `${t(lang, key as any)}: ${String(value)}`;
  }
}

// Bubbles container component
const BubblesContainer: React.FC<{ keywords?: string[]; actions?: string[]; extractedValues?: Record<string, any>; location?: string | null; employeeCount?: number | null; lang?: Lang }> = ({ keywords, actions, extractedValues, location, employeeCount, lang = 'en' }) => {
  if ((!keywords || keywords.length === 0) && (!actions || actions.length === 0) && (!extractedValues || Object.keys(extractedValues).length === 0)) {
    return null;
  }

  return (
    <div className="bubbles-container">
      {keywords && keywords.length > 0 && (
        <div className="bubble-section">
          <div className="bubble-label">Keywords:</div>
          <div className="bubble-list">
            {keywords.map((keyword, index) => (
              <span key={index} className="bubble keyword-bubble">{keyword}</span>
            ))}
          </div>
        </div>
      )}
      {actions && actions.length > 0 && (
        <div className="bubble-section">
          <div className="bubble-label">Actions:</div>
          <div className="bubble-list">
            {actions.map((action, index) => (
              <span key={index} className="bubble action-bubble">{action}</span>
            ))}
          </div>
        </div>
      )}
      {extractedValues && Object.keys(extractedValues).length > 0 && (
        <div className="bubble-section">
          <div className="bubble-label">{t(lang, 'extractedValues')}</div>
          <div className="bubble-list">
            {Object.entries(extractedValues)
              .filter(([key, value]) => value !== null && value !== undefined)
              .map(([key, value], index) => (
                <Bubble key={index} text={formatMeasurement(key, value, lang)} type="extracted" />
              ))}
          </div>
        </div>
      )}
      {/* Render location and employee count as bubbles if present */}
      {location && (
        <div className="bubble-section">
          <div className="bubble-list">
            <Bubble text={`location: ${location}`} type="extracted" />
          </div>
        </div>
      )}
      {employeeCount !== null && employeeCount !== undefined && (
        <div className="bubble-section">
          <div className="bubble-list">
            <Bubble text={`employees: ${employeeCount}`} type="extracted" />
          </div>
        </div>
      )}
    </div>
  );
};

export default BubblesContainer; 